import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { Helmet } from "react-helmet-async";
import { API } from "@/App";
import { ArrowLeft, Calendar, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_crm-news-hub/artifacts/srv2lps9_ChatGPT%20Image%20Apr%202%2C%202026%2C%2010_47_10%20AM.png";

const NewsDetailPage = () => {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logoUrl, setLogoUrl] = useState(LOGO_URL);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [postRes, contentRes] = await Promise.all([
          axios.get(`${API}/news/public/${postId}`),
          axios.get(`${API}/site-content/public`).catch(() => null),
        ]);
        setPost(postRes.data);
        if (contentRes?.data?.logo_url) setLogoUrl(contentRes.data.logo_url);
      } catch (err) {
        setError("Post not found");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [postId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F3] flex items-center justify-center">
        <p className="text-[#0C2E3C] text-lg">Loading...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-[#F0F4F3] flex flex-col items-center justify-center gap-4">
        <p className="text-[#0C2E3C] text-xl font-semibold">Post not found</p>
        <Link to="/">
          <Button className="bg-[#0C2E3C] text-[#F0F4F3] hover:bg-[#0C2E3C]/90 rounded-none font-bold uppercase tracking-wider text-sm">
            <ArrowLeft size={16} className="mr-2" /> Back to Home
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4F3]" data-testid="news-detail-page">
      <Helmet>
        <title>{post.title} | Commonwealth FC News</title>
        <meta name="description" content={post.excerpt || post.title} />
        <meta property="og:title" content={`${post.title} | Commonwealth FC`} />
        <meta property="og:description" content={post.excerpt || post.title} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://commonwealthfc.org/news/${postId}`} />
        {post.image_id && <meta property="og:image" content={`${API}/files/${post.image_id}`} />}
        <link rel="canonical" href={`https://commonwealthfc.org/news/${postId}`} />
      </Helmet>
      {/* Nav */}
      <nav className="bg-[#0C2E3C] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2" data-testid="news-nav-logo">
            <img src={logoUrl} alt="Commonwealth FC" className="h-9 w-auto" />
            <span className="text-sm font-bold uppercase tracking-wider text-[#F0F4F3] hidden sm:block">
              Commonwealth FC
            </span>
          </Link>
          <Link to="/" className="text-[#F0F4F3]/80 hover:text-[#C8E64E] text-sm font-medium transition-colors">
            <ArrowLeft size={16} className="inline mr-1" /> Back
          </Link>
        </div>
      </nav>

      {/* Article */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Featured Image */}
        {post.image_id && (
          <img
            src={`${API}/files/${post.image_id}`}
            alt={post.title}
            className={`w-full mb-8 ${
              post.image_fit === "contain" ? "max-h-[480px] object-contain"
                : post.image_fit === "fill" ? "h-64 sm:h-80 object-fill"
                : post.image_fit === "auto" ? "max-h-[480px] object-scale-down"
                : "h-64 sm:h-80 object-cover"
            }`}
            data-testid="news-detail-image"
          />
        )}

        {/* Meta */}
        <div className="flex items-center gap-3 text-[#6B8A8F] text-sm mb-4" data-testid="news-detail-meta">
          <Calendar size={14} />
          <time>{new Date(post.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</time>
        </div>

        {/* Title */}
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0C2E3C] mb-8" data-testid="news-detail-title">
          {post.title}
        </h1>

        {/* Content */}
        <div
          className="prose prose-lg max-w-none text-[#6B8A8F] leading-relaxed
            prose-headings:text-[#0C2E3C] prose-headings:font-bold
            prose-a:text-[#0C2E3C] prose-a:underline hover:prose-a:text-[#C8E64E]
            prose-strong:text-[#0C2E3C]"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content_html || `<p>${post.content}</p>`) }}
          data-testid="news-detail-content"
        />

        {/* External Link */}
        {post.link_url && (
          <div className="mt-8 pt-6 border-t border-[#0C2E3C]/10">
            <a
              href={post.link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#0C2E3C] font-bold text-sm hover:text-[#C8E64E] transition-colors"
              data-testid="news-detail-link"
            >
              <ExternalLink size={16} /> View External Link
            </a>
          </div>
        )}
      </article>

      {/* Simple Footer */}
      <footer className="bg-[#0C2E3C] py-6 text-center">
        <p className="text-[#F0F4F3]/40 text-sm">
          &copy; {new Date().getFullYear()} Commonwealth FC. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default NewsDetailPage;
